import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fillUrl } from "@/lib/formTypes";
import { ArchiveButton } from "@/components/ArchiveButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, FileSpreadsheet, Link2, Globe, Lock, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function Forms() {
  const nav = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/forms").then((r) => setForms(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api.post("/forms", { title: "Form tanpa judul", access: "internal", is_open: true,
        questions: [{ type: "short_text", label: "Pertanyaan pertama", required: false, options: [] }] });
      nav(`/form/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat form"); }
  };
  const copy = (id) => { navigator.clipboard?.writeText(fillUrl(id)); toast.success("Link form disalin"); };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Form & Rekap</h1>
          <p className="text-slate-500 text-sm">Buat formulir, bagikan link, dan unduh rekap jawaban ke Excel</p>
        </div>
        <Button onClick={create} data-testid="create-form-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" />Buat Form</Button>
      </div>
      {!loading && forms.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-slate-200" data-testid="forms-empty">
          <FileSpreadsheet className="h-10 w-10 mx-auto text-indigo-300 mb-2" />
          <p className="text-slate-500">Belum ada form. Klik "Buat Form" untuk memulai.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((f) => (
            <div key={f.id} data-testid={`form-card-${f.id}`} onClick={() => nav(`/form/${f.id}`)}
              className="group cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]">
              <div className="h-1.5 -mx-5 -mt-5 mb-4 rounded-t-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-slate-900 line-clamp-2">{f.title}</h3>
                <div onClick={(e) => e.stopPropagation()}>
                  <ArchiveButton url={`/forms/${f.id}`} label="form ini" onDone={load} testid={`archive-form-${f.id}`} />
                </div>
              </div>
              <p className="text-sm text-slate-500 line-clamp-2 mt-1 min-h-[2.5rem]">{f.description || "Tanpa deskripsi"}</p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Badge variant="outline" className="gap-1">{f.access === "public" ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}{f.access === "public" ? "Publik" : "Internal"}</Badge>
                <Badge className={f.is_open ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{f.is_open ? "Menerima jawaban" : "Ditutup"}</Badge>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className="text-sm text-slate-600 inline-flex items-center gap-1" data-testid={`form-count-${f.id}`}><Inbox className="h-4 w-4" />{f.response_count} jawaban</span>
                <button onClick={(e) => { e.stopPropagation(); copy(f.id); }} data-testid={`copy-link-${f.id}`} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"><Link2 className="h-3.5 w-3.5" />Salin link</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
